import moment from 'moment';
import ProspectStageType from '@/Mapper/ProspectStageType';
import { ProspectReminderTypeId } from '@/Mapper/ProspectActivityActionType';
import ProspectingHelpers from '@/helpers/prospecting-helpers';

const prospectsStore = (apiServices => ({
  strict: process.env.NODE_ENV !== 'production',
  namespaced: true,
  state: {
    // Dashboard Tabs / lists
    attentionProspects: { data: [], loaded: false },
    contactedProspects: { data: [], loaded: false },
    appraisalProspects: { data: [], loaded: false },

    // Stages
    prospectsInStageNew: { data: [], loaded: false },
    prospectsInStageAttempted: { data: [], loaded: false },
    prospectsInStageContacted: { data: [], loaded: false },
    prospectsInStageAppraised: { data: [], loaded: false },
    prospectsInStageListed: { data: [], loaded: false },
    prospectsInStageSold: { data: [], loaded: false },

    // Reminders
    prospectsWithFollowUpReminders: { data: [], loaded: false },
    prospectsWithAppraisalReminders: { data: [], loaded: false },

    // Overall Stats
    prospectStats: null,
    totalProspects: null,
    agentCallActivities: { data: [], loaded: false },
    agentPliteActivities: { data: [], loaded: false },
  },
  mutations: {
    setProspectsInStageNew: (state, Obj) => {
      state.prospectsInStageNew = Obj; // eslint-disable-line no-param-reassign
    },
    setAttentionProspects: (state, Obj) => {
      state.attentionProspects = Obj; // eslint-disable-line no-param-reassign
    },
    setContactedProspects: (state, Obj) => {
      state.contactedProspects = Obj; // eslint-disable-line no-param-reassign
    },
    setAppraisalProspects: (state, Obj) => {
      state.appraisalProspects = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsWithFollowUpReminders: (state, Obj) => {
      state.prospectsWithFollowUpReminders = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsWithAppraisalReminders: (state, Obj) => {
      state.prospectsWithAppraisalReminders = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsInStageContacted: (state, Obj) => {
      state.prospectsInStageContacted = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsInStageAttempted: (state, Obj) => {
      state.prospectsInStageAttempted = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsInStageAppraised: (state, Obj) => {
      state.prospectsInStageAppraised = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsInStageListed: (state, Obj) => {
      state.prospectsInStageListed = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectsInStageSold: (state, Obj) => {
      state.prospectsInStageSold = Obj; // eslint-disable-line no-param-reassign
    },
    setProspectStats: (state, Obj) => {
      state.prospectStats = Obj; // eslint-disable-line no-param-reassign
    },
    setTotalProspect: (state, Obj) => {
      state.totalProspects = Obj; // eslint-disable-line no-param-reassign
    },
    setAgentCallActivities: (state, Obj) => {
      state.agentCallActivities = Obj; // eslint-disable-line no-param-reassign
    },
    setAgentPliteActivities: (state, Obj) => {
      state.agentPliteActivities = Obj; // eslint-disable-line no-param-reassign
    },
  },
  actions: {
    async loadAttentionProspects(context, payload) {
      const followUps = this.state.prospectsStore.prospectsWithFollowUpReminders.loaded || context.dispatch('loadProspectsWithActiveFollowUps', payload);
      const bookedAppraisals = this.state.prospectsStore.prospectsWithAppraisalReminders.loaded || context.dispatch('loadProspectsWithActiveAppraisalReminders', payload);
      const attempted = this.state.prospectsStore.prospectsInStageAttempted.loaded || context.dispatch('loadProspectsWithStageAttempted', payload);

      Promise.all([
        followUps,
        bookedAppraisals,
        attempted,
      ]).then(() => {
        const reminders = this.state.prospectsStore.prospectsWithAppraisalReminders.data.concat(
          this.state.prospectsStore.prospectsWithFollowUpReminders.data,
        ).map((prospect) => {
          const p = prospect;
          p.activeReminder = ProspectingHelpers.getProspectsActivteReminder(prospect.reminders);
          return p;
        }).sort((a, b) => {
          if (a.activeReminder && b.activeReminder) {
            return new Date(b.activeReminder.reminderDate) - new Date(a.activeReminder.reminderDate);
          } else if (a.activeReminder) {
            return -1;
          } else if (b.activeReminder) {
            return 1;
          } else {
            return 0;
          }
        });

        const attemptedProspects = this.state.prospectsStore.prospectsInStageAttempted.data.filter(
          prospect => moment().diff(prospect.stage.createdDate, 'days') <= 7
          && moment().diff(prospect.stage.createdDate, 'hours') > 24 && ProspectingHelpers.getProspectsActivteReminder(prospect.reminders) === null,
        ).sort((a, b) => new Date(b.stage.createdDate) - new Date(a.stage.createdDate));

        Promise.all([
          reminders,
          attemptedProspects,
        ]).then(() => {
          const prospectsData = {
            data: reminders.concat(attemptedProspects),
            loaded: true,
          };
          context.commit('setAttentionProspects', prospectsData);
        });
      });
    },
    async loadContactedProspects(context, payload) {
      const stageContacted = this.state.prospectsStore.prospectsInStageContacted.loaded || context.dispatch('loadProspectsWithStageContacted', payload);
      const stageAppraised = this.state.prospectsStore.prospectsInStageAppraised.loaded || context.dispatch('loadProspectsWithStageAppraised', payload);
      const stageListed = this.state.prospectsStore.prospectsInStageListed.loaded || context.dispatch('loadProspectsWithStageListed', payload);
      const stageSold = this.state.prospectsStore.prospectsInStageSold.loaded || context.dispatch('loadProspectsWithStageSold', payload);

      Promise.all([
        stageContacted,
        stageAppraised,
        stageListed,
        stageSold,
      ]).then(() => {
        const allContacted = [];

        const prospectsData = {
          data: allContacted.concat(this.state.prospectsStore.prospectsInStageContacted.data,
            this.state.prospectsStore.prospectsInStageAppraised.data,
            this.state.prospectsStore.prospectsInStageListed.data,
            this.state.prospectsStore.prospectsInStageSold.data).map((prospect) => {
            const p = prospect;
            const activeReminder = ProspectingHelpers
              .getProspectsActivteReminder(prospect.reminders);
            p.lastModifiedDate = activeReminder ? activeReminder.reminderDate : p.stage.createdDate;
            return p;
          })
            .sort((a, b) => new Date(b.stage.lastModifiedDate)
            - new Date(a.stage.lastModifiedDate)),
          loaded: true,
        };

        console.log(prospectsData);

        context.commit('setContactedProspects', prospectsData);
      });
    },
    async loadAppraisalProspects(context, payload) {
      let appraisalsBooked = null;
      const appraisalsBookedPromise = apiServices.pp.prospect
        .getProspectsForAgentByReminder(payload.agentId,
          { reminderTypeId: ProspectReminderTypeId.bookedAppraisal })
        .then((prospects) => { appraisalsBooked = prospects; });
      const stageAppraised = this.state.prospectsStore.prospectsInStageAppraised.loaded || context.dispatch('loadProspectsWithStageAppraised', payload);

      Promise.all([
        appraisalsBookedPromise,
        stageAppraised,
      ]).then(() => {
        const allAppraisals = [];

        const prospectsData = {
          data: allAppraisals.concat(appraisalsBooked,
            this.state.prospectsStore.prospectsInStageAppraised.data).map((prospect) => {
            const p = prospect;
            const activeReminder = ProspectingHelpers
              .getProspectsActivteReminder(prospect.reminders);
            p.lastModifiedDate = activeReminder ? activeReminder.reminderDate : p.stage.createdDate;
            return p;
          })
            .sort((a, b) => new Date(b.stage.lastModifiedDate)
            - new Date(a.stage.lastModifiedDate)),
          loaded: true,
        };

        context.commit('setAppraisalProspects', prospectsData);
      });
    },
    async loadProspectsWithActiveFollowUps(context, payload) {
      context.commit('setProspectsWithFollowUpReminders', { data: [], loaded: false });

      const now = moment();

      const payloadData = {
        reminderTypeId: ProspectReminderTypeId.followUp,
        dateTo: now.format('YYYY-MM-DD'),
        dateFrom: now.subtract(7, 'd').format('YYYY-MM-DD'), // 7 days ago
      };

      return apiServices.pp.prospect.getProspectsForAgentByReminder(payload.agentId, payloadData)
        .then((prospects) => {
          if (prospects) {
            const prospectsData = {
              data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
              loaded: true,
            };

            context.commit('setProspectsWithFollowUpReminders', prospectsData);
          } else {
            context.commit('setProspectsWithFollowUpReminders', { data: [], loaded: true });
          }
        }, (err) => {
          context.commit('setProspectsWithFollowUpReminders', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadProspectsWithActiveAppraisalReminders(context, payload) {
      context.commit('setProspectsWithAppraisalReminders', { data: [], loaded: false });

      const now = moment();

      const payloadData = {
        reminderTypeId: ProspectReminderTypeId.bookedAppraisal,
        dateTo: now.format('YYYY-MM-DD'),
        dateFrom: now.subtract(7, 'd').format('YYYY-MM-DD'), // 7 days ago
      };

      return apiServices.pp.prospect.getProspectsForAgentByReminder(payload.agentId, payloadData)
        .then((prospects) => {
          if (prospects) {
            const prospectsData = {
              data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
              loaded: true,
            };

            context.commit('setProspectsWithAppraisalReminders', prospectsData);
          } else {
            context.commit('setProspectsWithAppraisalReminders', { data: [], loaded: true });
          }
        }, (err) => {
          context.commit('setProspectsWithAppraisalReminders', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadProspectsWithStageNew(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.new,
      };

      return apiServices.pp.prospect.getProspectsForAgentByStage(payload.agentId, payloadData)
        .then((prospects) => {
          const prospectsData = {
            data: prospects.sort((a, b) => b.chilliScore - a.chilliScore),
            loaded: true,
          };

          context.commit('setProspectsInStageNew', prospectsData);
        }, (err) => {
          context.commit('setProspectsInStageNew', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadProspectsWithStageContacted(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.contacted,
      };

      return apiServices.pp.prospect.getProspectsForAgentByStage(payload.agentId, payloadData)
        .then((prospects) => {
          const prospectsData = {
            data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
            loaded: true,
          };

          context.commit('setProspectsInStageContacted', prospectsData);
        }, (err) => {
          context.commit('setProspectsInStageContacted', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadProspectsWithStageAttempted(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.attempted,
      };

      return apiServices.pp.prospect.getProspectsForAgentByStage(payload.agentId, payloadData)
        .then((prospects) => {
          const prospectsData = {
            data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
            loaded: true,
          };

          context.commit('setProspectsInStageAttempted', prospectsData);
        }, (err) => {
          console.log(err);
        });
    },
    async loadProspectsWithStageAppraised(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.appraised,
      };

      return apiServices.pp.prospect.getProspectsForAgentByStage(payload.agentId, payloadData)
        .then((prospects) => {
          const prospectsData = {
            data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
            loaded: true,
          };

          context.commit('setProspectsInStageAppraised', prospectsData);
        }, (err) => {
          context.commit('setProspectsInStageAppraised', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadProspectsWithStageListed(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.listed,
      };

      return apiServices.pp.prospect.getProspectsForAgentByStage(payload.agentId, payloadData)
        .then((prospects) => {
          const prospectsData = {
            data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
            loaded: true,
          };

          context.commit('setProspectsInStageListed', prospectsData);
        }, (err) => {
          context.commit('setProspectsInStageListed', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadProspectsWithStageSold(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.sold,
      };

      return apiServices.pp.prospect.getProspectsForAgentByStage(payload.agentId, payloadData)
        .then((prospects) => {
          const prospectsData = {
            data: prospects.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate)),
            loaded: true,
          };

          context.commit('setProspectsInStageSold', prospectsData);
        }, (err) => {
          context.commit('setProspectsInStageSold', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadAllTimeProspectsWithStageNew(context, payload) {
      const payloadData = {
        stageTypeId: ProspectStageType.new,
      };

      return apiServices.pp.prospect.getAllTimeProspectsForAgentByStage(payload.agentId, payloadData)
        .then((data) => {
          if (data) {
            console.log(data);
            context.commit('setTotalProspect', data);
          }
        }, (err) => {
          // reject(err);
          console.log(err);
        });
    },
    async loadProspectsStats(context, payload) {
      const now = moment();

      const payloadData = {
        date: now.format('YYYY-MM-DD'),
      };

      return apiServices.pp.prospect.getStatsForAgent(payload.agentId, payloadData)
        .then((data) => {
          if (data) {
            context.commit('setProspectStats', data);
          }
        }, (err) => {
          // reject(err);
          console.log(err);
        });
    },
    async loadAgentCallActivities(context, payload) {
      
      return apiServices.pp.activity.getAssistantActivityByOAAgentId(payload.agentId, payload)
        .then((data) => {
          if (data) {
            const activityData = {
              data: data[0],
              loaded: true,
            };
            context.commit('setAgentCallActivities', activityData);
          }
        }, (err) => {
          // reject(err);
          context.commit('setAgentCallActivities', { data: [], loaded: true });
          console.log(err);
        });
    },
    async loadAgentPliteActivities(context, payload) {
      return apiServices.pp.activity.getPliteActivityByOAAgentId(payload.agentId, payload)
        .then((data) => {
          if (data) {
            const activityData = {
              data: data[0],
              loaded: true,
            };
            context.commit('setAgentPliteActivities', activityData);
          }
        }, (err) => {
          // reject(err);
          context.commit('setAgentPliteActivities', { data: [], loaded: true });
          console.log(err);
        });
    },
    async updateProspectStatusInList(context, payload) {
      console.log(payload);
      const activeStageTypeId = payload.prospect.stage.stageType.stageTypeId;

      const reloadStageList = context.dispatch('reloadStageList', { stageTypeId: activeStageTypeId });

      if (this.state.prospectsStore.prospectStats) {
        context.dispatch('loadProspectsStats', { agentId: this.state.agentStore.agent.id });
      }

      reloadStageList.then(() => {
        if ((activeStageTypeId !== ProspectStageType.attempted
                || activeStageTypeId !== ProspectStageType.new)
              && this.state.prospectsStore.contactedProspects.loaded) {
          context.dispatch('loadContactedProspects', { agentId: this.state.agentStore.agent.id });
        }

        if ((activeStageTypeId !== ProspectStageType.attempted
          || activeStageTypeId !== ProspectStageType.new)
        && this.state.prospectsStore.attentionProspects.loaded) {
          context.dispatch('loadAttentionProspects', { agentId: this.state.agentStore.agent.id });
        }

        if (this.state.prospectsStore.appraisalProspects.loaded) {
          context.dispatch('loadAppraisalProspects', { agentId: this.state.agentStore.agent.id });
        }
      });
    },
    async updateProspectStageInList(context, payload) {
      const newStage = payload.newStage.stageType.stageTypeId;
      const oldStage = payload.prospect.stage.stageType.stageTypeId;

      const reloadOldStageList = context.dispatch('reloadStageList', { stageTypeId: newStage });
      const reloadNewStageList = context.dispatch('reloadStageList', { stageTypeId: oldStage });

      if (this.state.prospectsStore.prospectStats) {
        context.dispatch('loadProspectsStats', { agentId: this.state.agentStore.agent.id });
      }

      Promise.all([
        reloadOldStageList,
        reloadNewStageList,
      ]).then(() => {
        if ((newStage !== ProspectStageType.attempted
            || oldStage !== ProspectStageType.attempted || oldStage !== ProspectStageType.new)
        && this.state.prospectsStore.contactedProspects.loaded) {
          context.dispatch('loadContactedProspects', { agentId: this.state.agentStore.agent.id });
        }

        if ((newStage === ProspectStageType.attempted
            || oldStage === ProspectStageType.attempted || oldStage === ProspectStageType.new)
          && this.state.prospectsStore.attentionProspects.loaded) {
          context.dispatch('loadAttentionProspects', { agentId: this.state.agentStore.agent.id });
        }

        if (this.state.prospectsStore.appraisalProspects.loaded) {
          context.dispatch('loadAppraisalProspects', { agentId: this.state.agentStore.agent.id });
        }
      });
    },
    async updateProspectReminderInList(context, payload) {
      const activeStageTypeId = payload.prospect.stage.stageType.stageTypeId;

      if (this.state.prospectsStore.prospectStats) {
        context.dispatch('loadProspectsStats', { agentId: this.state.agentStore.agent.id });
      }

      if (payload.newReminder) {
        const reloadStageList = context.dispatch('reloadStageList', { stageTypeId: activeStageTypeId });

        reloadStageList.then(() => {
          if ((activeStageTypeId !== ProspectStageType.attempted
                || activeStageTypeId !== ProspectStageType.new)
              && this.state.prospectsStore.contactedProspects.loaded) {
            context.dispatch('loadContactedProspects', { agentId: this.state.agentStore.agent.id });
          }
        });
      } else if (payload.updatedReminder) {
        const reloadStageList = context.dispatch('reloadStageList', { stageTypeId: activeStageTypeId });
        const reloadReminderList = context.dispatch('reloadStageList', { reminderTypeId: payload.updatedReminder.reminderType.reminderTypeId });

        Promise.all([
          reloadStageList,
          reloadReminderList,
        ]).then(() => {
          if ((activeStageTypeId !== ProspectStageType.attempted
            || activeStageTypeId !== ProspectStageType.new)
          && this.state.prospectsStore.contactedProspects.loaded) {
            context.dispatch('loadContactedProspects', { agentId: this.state.agentStore.agent.id });
          }

          if (this.state.prospectsStore.attentionProspects.loaded) {
            context.dispatch('loadAttentionProspects', { agentId: this.state.agentStore.agent.id });
          }

          if (this.state.prospectsStore.appraisalProspects.loaded) {
            context.dispatch('loadAppraisalProspects', { agentId: this.state.agentStore.agent.id });
          }
        });
      }
    },
    async reloadStageList(context, payload) {
      const reloadData = {
        agentId: this.state.agentStore.agent.id,
      };

      switch (payload.stageTypeId) {
        case ProspectStageType.contacted:
          return !this.state.prospectsStore.prospectsInStageContacted.loaded || context.dispatch('loadProspectsWithStageContacted', reloadData);
        case ProspectStageType.attempted:
          return !this.state.prospectsStore.prospectsInStageAttempted.loaded || context.dispatch('loadProspectsWithStageAttempted', reloadData);
        case ProspectStageType.new:
          return !this.state.prospectsStore.prospectsInStageNew.loaded || context.dispatch('loadProspectsWithStageNew', reloadData);
        case ProspectStageType.appraised:
          return !this.state.prospectsStore.prospectsInStageAppraised.loaded || context.dispatch('loadProspectsWithStageAppraised', reloadData);
        case ProspectStageType.listed:
          return !this.state.prospectsStore.prospectsInStageListed.loaded || context.dispatch('loadProspectsWithStageListed', reloadData);
        case ProspectStageType.sold:
          return !this.state.prospectsStore.prospectsInStageSold.loaded || context.dispatch('loadProspectsWithStageSold', reloadData);
        case ProspectStageType.notAttempted:
          return !this.state.prospectsStore.prospectsInStageNew.loaded || context.dispatch('loadProspectsWithStageNew', reloadData);
        default:
          return true;
      }
    },
  },
  getters: {
    contactedFilterOptions: (state) => {
      const options = [{
        value: 'all', text: 'All', selected: true,
      }];

      if (state.prospectsInStageContacted.data.length > 0) {
        options.push({
          value: `${ProspectStageType.contacted}-stage`, text: 'Contacted', selected: false,
        });
      }

      if (state.prospectsInStageAppraised.data.length > 0) {
        options.push({
          value: `${ProspectStageType.appraised}-stage`, text: 'Appraised', selected: false,
        });
      }

      if (state.prospectsInStageListed.data.length > 0) {
        options.push({
          value: `${ProspectStageType.listed}-stage`, text: 'Listed', selected: false,
        });
      }

      if (state.prospectsInStageSold.data.length > 0) {
        options.push({
          value: `${ProspectStageType.sold}-stage`, text: 'Sold', selected: false,
        });
      }

      const findActiveAReminders = state.contactedProspects.data
        .filter(prospect => ProspectingHelpers
          .getProspectsActivteReminder(prospect.reminders) !== null);

      const hasActiveFollowUp = findActiveAReminders
        .find(prospect => ProspectingHelpers
          .getProspectsActivteReminder(prospect.reminders).reminderType.reminderTypeId
      === ProspectReminderTypeId.followUp) || false;

      const hasActiveApprasial = findActiveAReminders
        .find(prospect => ProspectingHelpers
          .getProspectsActivteReminder(prospect.reminders).reminderType.reminderTypeId
        === ProspectReminderTypeId.bookedAppraisal) || false;

      if (hasActiveFollowUp) {
        options.push({
          value: `${ProspectReminderTypeId.followUp}-reminder`, text: 'Follow-up booked', selected: false,
        });
      }

      if (hasActiveApprasial) {
        options.push({
          value: `${ProspectReminderTypeId.bookedAppraisal}-reminder`, text: 'Apprasial booked', selected: false,
        });
      }

      return options;
    },
    attemptedFilterOptions: (state) => {
      const options = [{
        value: 'all', text: 'All', selected: true,
      }];

      if (state.prospectsInStageAttempted.data.length > 0) {
        options.push({
          value: `${ProspectStageType.attempted}-stage`, text: 'Attempted', selected: false,
        });
      }

      const findActiveAReminders = state.prospectsInStageAttempted.data
        .filter(prospect => ProspectingHelpers
          .getProspectsActivteReminder(prospect.reminders) !== null);

      const hasActiveFollowUp = findActiveAReminders
        .find(prospect => ProspectingHelpers
          .getProspectsActivteReminder(prospect.reminders).reminderType.reminderTypeId
      === ProspectReminderTypeId.followUp) || false;

      if (hasActiveFollowUp) {
        options.push({
          value: `${ProspectReminderTypeId.followUp}-reminder`, text: 'Follow-up booked', selected: false,
        });
      }

      return options;
    },
  },
}));

export default prospectsStore;
